import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { TranslationService } from './translation.service';

/**
 * Run `refetch` after the active language has changed **and been applied**.
 *
 * For everything the **server** resolves into one language — select options (`getForSelect`),
 * grouped reference lists, resolver-fed page headers, anything read with `?lang=` — the rows
 * in hand carry no other language, so the only way to follow a switch is to ask for them
 * again. (A record fetched with `?withTranslations=true` needs none of this: it holds every
 * language, and `toDisplayScalar` re-resolves it on read.)
 *
 * It hangs off `TranslationService.languageApplied`, not the `language` signal, for two
 * reasons. The signal flips before `translateService.use()` has loaded the new bundle, so a
 * refresh driven by it repaints the screen with the outgoing strings; and running this work
 * from inside an `effect` puts a navigation and a burst of HTTP calls in the middle of a
 * change-detection flush. The stream emits after the switch is complete, on its own tick.
 *
 * Call it from an injection context — a constructor or a field initializer — passing the
 * service in through normal constructor DI. The subscription ends with the caller.
 */
export function refetchOnLanguageChange(
  translation: TranslationService,
  refetch: () => void
): Subscription {
  return translation.languageApplied.pipe(takeUntilDestroyed()).subscribe(() => refetch());
}
